import { useState } from 'react'
import type { InsuranceId } from '../types'
import { InsuranceIcon } from '../icons/InsuranceIcon'
import type { StepInputProps } from './StepInput'

const options: { id: InsuranceId; label: string }[] = [
  { id: 'bil', label: 'Bil' },
  { id: 'indbo', label: 'Indbo' },
  { id: 'ulykke', label: 'Ulykke' },
  { id: 'hus', label: 'Hus' },
  { id: 'hund', label: 'Hund' },
  { id: 'andre', label: 'Andre' },
]

// Bundle-rabat-regel: indbo + 2 andre konkrete forsikringer = 15% samlerabat.
// "Andre" tæller IKKE — den er for ukendte/ikke-listede produkter.
const BUNDLE_PCT = 15
const BUNDLE_MIN = 3
const BUNDLE_ELIGIBLE: InsuranceId[] = ['bil', 'indbo', 'ulykke', 'hus', 'hund']

interface Status { text: string; tone: 'hint' | 'progress' | 'active'; credits: number }

function computeStatus(selected: InsuranceId[]): Status | null {
  if (selected.includes('ingen')) return null
  const hasIndbo = selected.includes('indbo')
  const eligible = selected.filter((i) => BUNDLE_ELIGIBLE.includes(i)).length
  // Credits = how many bundle requirements satisfied. Indbo is gating, så
  // ingen credits før den er valgt — også selvom man har 3 andre.
  const credits = hasIndbo ? Math.min(eligible, BUNDLE_MIN) : 0

  if (credits >= BUNDLE_MIN) {
    return { text: `${BUNDLE_PCT}% samlerabat aktiveret`, tone: 'active', credits }
  }
  if (!hasIndbo) {
    if (eligible === 0) return { text: `Indbo + 2 andre = ${BUNDLE_PCT}% samlerabat`, tone: 'hint', credits }
    const moreNeeded = Math.max(0, BUNDLE_MIN - 1 - eligible)
    if (moreNeeded === 0) return { text: `Tilføj indbo for ${BUNDLE_PCT}% samlerabat`, tone: 'progress', credits }
    return { text: `Tilføj indbo + ${moreNeeded} mere for ${BUNDLE_PCT}% samlerabat`, tone: 'hint', credits }
  }
  // hasIndbo, men ikke nok credits
  const moreNeeded = BUNDLE_MIN - credits
  if (moreNeeded === 1) return { text: `Vælg 1 mere for ${BUNDLE_PCT}% samlerabat`, tone: 'progress', credits }
  return { text: `Vælg ${moreNeeded} mere for ${BUNDLE_PCT}% samlerabat`, tone: 'hint', credits }
}

export function InsurancesInput({ state, onSubmit }: StepInputProps) {
  const [selected, setSelected] = useState<InsuranceId[]>(state.insurances ?? [])

  const toggle = (id: InsuranceId) => {
    setSelected((prev) => {
      if (id === 'ingen') return prev.includes('ingen') ? [] : ['ingen']
      const without = prev.filter((i) => i !== 'ingen')
      return without.includes(id) ? without.filter((i) => i !== id) : [...without, id]
    })
  }

  const status = computeStatus(selected)
  const noneSelected = selected.includes('ingen')

  return (
    <div className="input-dock">
      <div className="insurance-grid">
        {options.map((opt) => (
          <button
            key={opt.id}
            type="button"
            className={`insurance-option ${selected.includes(opt.id) ? 'selected' : ''}`}
            onClick={() => toggle(opt.id)}
          >
            <span className="insurance-icon">
              <InsuranceIcon name={opt.id} />
            </span>
            <span className="insurance-label">{opt.label}</span>
          </button>
        ))}
      </div>
      {status && (
        <div className={`bundle-meter bundle-meter-${status.tone}`} role="status" aria-live="polite">
          <span className="bundle-dots" aria-hidden="true">
            {Array.from({ length: BUNDLE_MIN }).map((_, i) => (
              <span key={i} className={`bundle-dot ${i < status.credits ? 'filled' : ''}`} />
            ))}
          </span>
          <span className="bundle-meter-text">{status.text}</span>
        </div>
      )}
      <button
        type="button"
        className={`chip-option none-row ${noneSelected ? 'selected' : ''}`}
        onClick={() => toggle('ingen')}
      >
        <span style={{ width: 20, height: 20, display: 'inline-flex' }}>
          <InsuranceIcon name="ingen" />
        </span>
        <span>Jeg har ingen forsikring endnu</span>
      </button>
      <button
        className="btn-send full"
        disabled={selected.length === 0}
        onClick={() => onSubmit({ insurances: selected })}
      >
        Videre
      </button>
    </div>
  )
}
